// Backend-Schemas im Frontend abgebildet. Müssen beim Schema-Update mitlaufen.

// 'ADMIN_VIEWER' ist der Nur-Lesen-Zugang zum Panel (CUR-995).
export type UserRole = 'USER' | 'ADMIN_VIEWER' | 'REVIEWER' | 'ADMIN';

export type SubscriptionTier =
  | 'FREE'
  | 'PREMIUM_MONTHLY'
  | 'PREMIUM_YEARLY'
  | 'PREMIUM_PLUS_MONTHLY'
  | 'PREMIUM_PLUS_YEARLY';

// Registry der Einmalkauf-Add-ons — gespiegelt von Backend `KNOWN_ADDONS`
// (src/lib/topicScope.ts). Bei Schema-Update mitführen.
export const KNOWN_ADDONS = [{ key: 'praxis', label: 'Praxis-Paket' }] as const;
export type AddonKey = (typeof KNOWN_ADDONS)[number]['key'];

export interface UserListItem {
  id: string;
  displayName: string | null;
  email: string;
  role: UserRole;
  emailVerified: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  suspendedAt: string | null;
  deletedAt: string | null;
  subscription: {
    tier: SubscriptionTier;
    isTrialActive: boolean;
    expiresAt: string | null;
  } | null;
}

export interface UserListResponse {
  items: UserListItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

// Cross-App-Entitlement eines Nutzers (Multi-App: ein Account, mehrere Apps).
// Gespiegelt vom Backend `getUserAppEntitlements` (src/lib/userAppEntitlements.ts).
export interface UserAppEntitlement {
  app: string;
  tier: SubscriptionTier;
  platform: string | null;
  expiresAt: string | null;
  isTrialActive: boolean;
  trialEndsAt: string | null;
  firstPurchaseAt: string | null;
  addons: string[];
  updatedAt: string;
}

export interface UserDetail extends Omit<UserListItem, 'subscription'> {
  authProvider: string;
  suspendedReason: string | null;
  lastAppVersion: string | null;
  // Public-Author-Profile (Article-CMS, /team/<slug>)
  publicSlug: string | null;
  publicTitle: string | null;
  publicBio: string | null;
  publicAvatarUrl: string | null;
  credentials: string[];
  socialLinks: Record<string, string> | null;
  profile: {
    priorKnowledge: string | null;
    weeklyHours: number | null;
    targetExamDate: string | null;
    targetExamType: string | null;
    attendsSchool: boolean;
    schoolSync: string | null;
    schoolStartDate: string | null;
    schoolDurationMonths: number | null;
  } | null;
  // Existenz = "Stundenplan verknüpft" (CUR-407) — unabhängig von profile.attendsSchool,
  // beide Signale können auseinanderlaufen.
  schoolSchedule: {
    source: string;
    extractedAt: string;
  } | null;
  subscription: {
    tier: SubscriptionTier;
    platform: string | null;
    expiresAt: string | null;
    isTrialActive: boolean;
    trialEndsAt: string | null;
    firstPurchaseAt: string | null;
    revenueCatId: string;
    updatedAt: string;
    addons: string[];
  } | null;
  // Alle App-Entitlements des Nutzers (additiv neben `subscription`). Optional,
  // damit ein noch nicht deploytes Backend nicht den Type bricht.
  apps?: UserAppEntitlement[];
  _count: {
    progress: number;
    quizResults: number;
    studySessions: number;
    deviceTokens: number;
  };
  tokenStats: {
    lifetime: number;
    last30Sum: number;
    last30Days?: { day: string; tokens: number }[];
  } | null;
}

export type DevicePlatform = 'IOS' | 'ANDROID' | 'STRIPE';

export interface UserDevice {
  id: string;
  platform: DevicePlatform;
  deviceId: string | null;
  tokenPreview: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuditEntry {
  id: string;
  action: string;
  before: unknown;
  after: unknown;
  metadata: { reason?: string; [key: string]: unknown } | null;
  ipAddress: string | null;
  stepUpUsed: boolean;
  createdAt: string;
  user: {
    id: string;
    displayName: string | null;
    email: string;
  } | null;
}

export interface CronUserEvent {
  id: string;
  runId: string | null;
  jobName: string;
  action: string;
  reason: string | null;
  payload: Record<string, unknown> | null;
  createdAt: string;
  run: {
    startedAt: string;
    status: string;
    durationMs: number | null;
  } | null;
}

export interface CronUserEventsResponse {
  events: CronUserEvent[];
  nextCursor: string | null;
  availableJobNames: string[];
}

export interface CronUserEventsFilters {
  jobName?: string;
  action?: string;
  since?: string;
  until?: string;
  cursor?: string | null;
  limit?: number;
}
